// Utskick och avsändare (designen Admin-Utskick, ADR 0068): när offerten
// skickas, när den granskas, vem som står som avsändare, och byråns egen
// avsändardomän med DKIM-posterna.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { ApiFel, data } from '../api/client.js';
import type { components } from '../api/schema.js';
import { useAuth, useMe } from '../auth/auth.js';
import { Sida } from '../layout/Sida.js';

type Installningar = components['schemas']['Settings'];
type Dispatch = Installningar['dispatch'];
type Avsandare = components['schemas']['Sender'];

type Lage = Dispatch['mode'];
type Fordrojning = '15' | '60' | 'nextWorkday';

const LAGEN: { kod: Lage; namn: string; text: string }[] = [
  {
    kod: 'direct',
    namn: 'Direkt',
    text: 'Kunden ser offerten i formuläret och får den med e-post på några sekunder.',
  },
  {
    kod: 'delayed',
    namn: 'Fördröjt',
    text: 'Offerten väntar en stund innan den skickas. Under tiden kan ni ändra eller stoppa den.',
  },
  {
    kod: 'approve_first',
    namn: 'Godkänn först',
    text: 'Ni får den färdiga offerten och godkänner den innan den går ut.',
  },
];

const FORDROJNINGAR: { kod: Fordrojning; namn: string }[] = [
  { kod: '15', namn: '15 minuter' },
  { kod: '60', namn: 'En timme' },
  { kod: 'nextWorkday', namn: 'Nästa arbetsdag kl. 08' },
];

function fordrojningFran(d: Dispatch): Fordrojning {
  if (d.mode !== 'delayed') return '15';
  if (d.delay.type === 'nextWorkday') return 'nextWorkday';
  return d.delay.minutes >= 60 ? '60' : '15';
}

export function dispatchFran(lage: Lage, fordrojning: Fordrojning): Dispatch {
  if (lage !== 'delayed') return { mode: lage };
  return fordrojning === 'nextWorkday'
    ? { mode: 'delayed', delay: { type: 'nextWorkday' } }
    : { mode: 'delayed', delay: { type: 'minutes', minutes: Number(fordrojning) } };
}

interface Kontakt {
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  privacyUrl: string;
}

const KONTAKTFALT: { nyckel: keyof Kontakt; etikett: string; typ?: string; hjalp?: string }[] = [
  { nyckel: 'contactName', etikett: 'Kontaktperson' },
  { nyckel: 'contactTitle', etikett: 'Titel' },
  {
    nyckel: 'contactEmail',
    etikett: 'Svar från kunden går till',
    typ: 'email',
    hjalp:
      'Kunden kan svara direkt på offertmejlet och uppföljningen, och svaret hamnar här. Lämnar ni fältet tomt går svaren till byråns ägare.',
  },
  { nyckel: 'contactPhone', etikett: 'Telefon', typ: 'tel' },
  { nyckel: 'address', etikett: 'Adress' },
  {
    nyckel: 'privacyUrl',
    etikett: 'Er integritetspolicy',
    typ: 'url',
    hjalp: 'Formuläret på er webbplats länkar hit.',
  },
];

export function Utskick() {
  const me = useMe();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const kanAndra = me.user.role === 'owner';
  const id = useId();

  const installningar = useQuery({
    queryKey: ['installningar'],
    queryFn: async () => data(await api.GET('/v1/admin/branding')),
  });

  const [lage, setLage] = useState<Lage | null>(null);
  const [fordrojning, setFordrojning] = useState<Fordrojning>('15');
  const [troskel, setTroskel] = useState('70');
  const [kontakt, setKontakt] = useState<Kontakt | null>(null);
  const [kvitto, setKvitto] = useState(false);

  useEffect(() => {
    const d = installningar.data;
    if (!d || lage !== null) return;
    setLage(d.dispatch.mode);
    setFordrojning(fordrojningFran(d.dispatch));
    setTroskel(String(d.confidenceThresholdPercent));
    const b = d.branding;
    setKontakt({
      contactName: b.contactName ?? '',
      contactTitle: b.contactTitle ?? '',
      contactEmail: b.contactEmail ?? '',
      contactPhone: b.contactPhone ?? '',
      address: b.address ?? '',
      privacyUrl: b.privacyUrl ?? '',
    });
  }, [installningar.data, lage]);

  const spara = useMutation({
    mutationFn: async (kropp: {
      dispatch: Dispatch;
      confidenceThresholdPercent: number;
      kontakt: Kontakt;
    }) => {
      const nu = installningar.data?.branding;
      if (!nu) throw new Error('Inställningarna saknas');
      // Hela varumärket ersätts, så de andra fälten följer med som de är.
      // Tomma kontaktfält skickas inte.
      const kontaktnycklar = new Set<string>(Object.keys(kropp.kontakt));
      const branding = Object.fromEntries([
        ...Object.entries(nu).filter(([nyckel]) => !kontaktnycklar.has(nyckel)),
        ...Object.entries(kropp.kontakt)
          .map(([nyckel, varde]) => [nyckel, varde.trim()] as const)
          .filter(([, varde]) => varde !== ''),
      ]);
      return data(
        await api.PUT('/v1/admin/branding', {
          body: {
            branding: branding as Installningar['branding'],
            dispatch: kropp.dispatch,
            confidenceThresholdPercent: kropp.confidenceThresholdPercent,
          },
        }),
      );
    },
    onSuccess: (svar) => {
      queryClient.setQueryData(['installningar'], svar);
      setKvitto(true);
    },
  });

  if (installningar.isError) {
    return (
      <Sida titel="Utskick">
        <div className="meddelande" role="alert">
          Inställningarna gick inte att hämta. Ladda om sidan och försök igen.
        </div>
      </Sida>
    );
  }
  if (!installningar.data || lage === null || !kontakt) {
    return (
      <Sida titel="Utskick">
        <p role="status">Hämtar inställningarna …</p>
      </Sida>
    );
  }

  const troskelTal = Number(troskel);
  const troskelOk = /^\d{1,3}$/.test(troskel.trim()) && troskelTal >= 0 && troskelTal <= 100;

  function skicka(e: FormEvent) {
    e.preventDefault();
    setKvitto(false);
    if (!troskelOk || !lage || !kontakt) return;
    spara.mutate({
      dispatch: dispatchFran(lage, fordrojning),
      confidenceThresholdPercent: troskelTal,
      kontakt,
    });
  }

  return (
    <Sida titel="Utskick">
      <p className="ingress katalog-huvud">
        Offerten räknas alltid fram direkt. Här väljer ni när och från vem den skickas.
      </p>
      {!kanAndra && (
        <div className="kort notis" role="note">
          Bara byråns ägare kan ändra utskicken.
        </div>
      )}
      <form className="utskick" onSubmit={skicka} noValidate>
        <fieldset className="kort flikkort panel-falt" disabled={!kanAndra || spara.isPending}>
          <legend className="avsnitt-rubrik legend-rubrik">När ska offerten skickas?</legend>
          <div className="val-lista lagen" role="radiogroup" aria-label="Utskicksläge">
            {LAGEN.map((l) => (
              <label key={l.kod} className={`val${lage === l.kod ? ' valt' : ''}`}>
                <input
                  type="radio"
                  name="lage"
                  checked={lage === l.kod}
                  onChange={() => {
                    setKvitto(false);
                    setLage(l.kod);
                  }}
                />
                <span>
                  <span className="val-namn">{l.namn}</span>
                  <span className="val-text">{l.text}</span>
                </span>
              </label>
            ))}
          </div>
          {lage === 'delayed' && (
            <div className="falt smal">
              <label className="etikett" htmlFor={`${id}-ford`}>
                Vänta
              </label>
              <select
                id={`${id}-ford`}
                className="in"
                value={fordrojning}
                onChange={(e) => setFordrojning(e.target.value as Fordrojning)}
              >
                {FORDROJNINGAR.map((f) => (
                  <option key={f.kod} value={f.kod}>
                    {f.namn}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="falt smal">
            <label className="etikett" htmlFor={`${id}-troskel`}>
              Granska om AI:s säkerhet är under
            </label>
            <div className="med-enhet">
              <input
                id={`${id}-troskel`}
                className="in mono tal"
                inputMode="numeric"
                aria-invalid={troskelOk ? undefined : true}
                aria-describedby={`${id}-troskel-hjalp`}
                value={troskel}
                onChange={(e) => setTroskel(e.target.value)}
              />
              <span>%</span>
            </div>
            <div className={troskelOk ? 'hjalp' : 'faltfel'} id={`${id}-troskel-hjalp`}>
              {troskelOk
                ? 'Förfrågningar som går till granskning väntar alltid på er, oavsett läge.'
                : 'Skriv ett tal mellan 0 och 100.'}
            </div>
          </div>
        </fieldset>
        <fieldset className="kort flikkort panel-falt" disabled={!kanAndra || spara.isPending}>
          <legend className="avsnitt-rubrik legend-rubrik">Avsändare i offerten</legend>
          <div className="tva">
            {KONTAKTFALT.map((f) => (
              <div className="falt" key={f.nyckel}>
                <label className="etikett" htmlFor={`${id}-${f.nyckel}`}>
                  {f.etikett}
                </label>
                <input
                  id={`${id}-${f.nyckel}`}
                  className="in"
                  type={f.typ ?? 'text'}
                  value={kontakt[f.nyckel]}
                  {...(f.hjalp ? { 'aria-describedby': `${id}-${f.nyckel}-hjalp` } : {})}
                  onChange={(e) => {
                    setKvitto(false);
                    setKontakt({ ...kontakt, [f.nyckel]: e.target.value });
                  }}
                />
                {f.hjalp && (
                  <div className="hjalp" id={`${id}-${f.nyckel}-hjalp`}>
                    {f.hjalp}
                  </div>
                )}
              </div>
            ))}
          </div>
        </fieldset>
        {spara.isError && (
          <div className="meddelande" role="alert">
            {spara.error instanceof ApiFel && spara.error.status === 400
              ? 'Något fält är inte giltigt. Kontrollera e-postadressen och länken.'
              : 'Det gick inte att spara. Försök igen om en stund.'}
          </div>
        )}
        <div className="levande" aria-live="polite">
          {kvitto && (
            <div className="kvitto">
              Utskicken är sparade. Formuläret på er webbplats använder dem inom fem minuter.
            </div>
          )}
        </div>
        {kanAndra && (
          <div className="panel-fot slut">
            <button type="submit" className="btn btn-p" disabled={spara.isPending || !troskelOk}>
              {spara.isPending ? 'Sparar …' : 'Spara'}
            </button>
          </div>
        )}
      </form>
      <Doman kanAndra={kanAndra} />
    </Sida>
  );
}

const STATUS = (found: boolean | null) =>
  found === null ? 'Inte kontrollerad' : found ? 'Hittad' : 'Väntar';

/** Instruktionerna till byråns IT, som ett mejl med posterna. */
function instruktioner(s: NonNullable<Avsandare['senderDomain']>): string {
  const rader = s.records.map((r) => `${r.type}  ${r.name}  ->  ${r.value}`).join('\n');
  const text = `Hej!\n\nVi vill skicka offerter från ${s.address}. Lägg in de här tre DNS-posterna för ${s.domain}:\n\n${rader}\n\nTack!`;
  return `mailto:?subject=${encodeURIComponent(`DNS-poster för ${s.domain}`)}&body=${encodeURIComponent(text)}`;
}

function Doman({ kanAndra }: { kanAndra: boolean }) {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const id = useId();
  const [domain, setDomain] = useState('');
  const [localPart, setLocalPart] = useState('offert');
  const [kopierad, setKopierad] = useState<string | null>(null);

  const avsandare = useQuery({
    queryKey: ['avsandare'],
    queryFn: async () => data(await api.GET('/v1/admin/sender-domain')),
  });
  const satt = (svar: Avsandare) => queryClient.setQueryData(['avsandare'], svar);

  const starta = useMutation({
    mutationFn: async () =>
      data(await api.POST('/v1/admin/sender-domain', { body: { domain, localPart } })),
    onSuccess: satt,
  });
  const kontrollera = useMutation({
    mutationFn: async () => data(await api.POST('/v1/admin/sender-domain/check')),
    onSuccess: satt,
  });
  const taBort = useMutation({
    mutationFn: async () => data(await api.DELETE('/v1/admin/sender-domain')),
    onSuccess: satt,
  });

  async function kopiera(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setKopierad(text);
    } catch {
      setKopierad(null);
    }
  }

  if (!avsandare.data) return null;
  const s = avsandare.data.senderDomain;
  const fallback = avsandare.data.fallbackAddress;

  return (
    <section className="kort flikkort" aria-labelledby={`${id}-rubrik`}>
      <div className="resultat-status">
        <h2 id={`${id}-rubrik`} className="avsnitt-rubrik">
          Egen avsändaradress
        </h2>
        {s && (
          <span className={`chip ${s.verified ? 'chip-gron' : 'chip-gul'}`}>
            {s.verified ? 'Domänen är verifierad' : 'Domänen verifieras'}
          </span>
        )}
      </div>
      {!s ? (
        <>
          <p className="ingress">
            Nu skickas offerterna från <span className="mono">{fallback}</span> med ert namn som
            avsändare. Med er egen domän skickas de från till exempel offert@er-byra.se.
          </p>
          {kanAndra && (
            <form
              className="domanform"
              onSubmit={(e) => {
                e.preventDefault();
                starta.mutate();
              }}
            >
              <div className="falt">
                <label className="etikett" htmlFor={`${id}-lokal`}>
                  Skicka från
                </label>
                <div className="med-enhet">
                  <input
                    id={`${id}-lokal`}
                    className="in"
                    value={localPart}
                    onChange={(e) => setLocalPart(e.target.value)}
                  />
                  <span>@</span>
                  <input
                    className="in"
                    aria-label="Er domän"
                    placeholder="er-byra.se"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-s" disabled={starta.isPending || !domain}>
                {starta.isPending ? 'Skapar posterna …' : 'Skapa DNS-posterna'}
              </button>
            </form>
          )}
          {starta.isError && (
            <div className="meddelande" role="alert">
              {starta.error instanceof ApiFel && starta.error.status === 400
                ? 'Skriv byråns egen domän, till exempel alvsangredovisning.se, och adressens första del, till exempel offert.'
                : 'Det gick inte att skapa posterna. Försök igen om en stund.'}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="ingress">
            {s.verified ? (
              <>
                Offerterna skickas från <span className="mono">{s.address}</span>.
              </>
            ) : (
              <>
                Lägg in de här tre posterna hos er domänleverantör. Tills de är klara skickas
                offerter från <span className="mono">{fallback}</span> med ert namn som avsändare.
              </>
            )}
          </p>
          <div
            className="tabellkort tabellrulle"
            tabIndex={0}
            role="region"
            aria-label={`DNS-poster för ${s.domain}`}
          >
            <table className="tabell">
              <caption className="visuellt-dold">DNS-poster för {s.domain}</caption>
              <thead>
                <tr>
                  <th scope="col">Typ</th>
                  <th scope="col">Namn</th>
                  <th scope="col">Värde</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="visuellt-dold">Kopiera</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.records.map((r, i) => (
                  <tr key={r.name}>
                    <td className="mono">{r.type}</td>
                    <td className="mono bryt">{r.name}</td>
                    <td className="mono bryt">{r.value}</td>
                    <td>
                      <span className={`chip ${r.found ? 'chip-gron' : 'chip-gul'}`}>
                        {STATUS(r.found)}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-g"
                        aria-label={`Kopiera post ${i + 1}`}
                        onClick={() => void kopiera(`${r.name} CNAME ${r.value}`)}
                      >
                        Kopiera
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="levande" aria-live="polite">
            {kopierad && <span className="hjalp">Posten är kopierad.</span>}
          </div>
          <div className="panel-fot">
            <span className="hjalp">
              {s.checkedAt
                ? `Senast kontrollerad ${new Date(s.checkedAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}. `
                : ''}
              Det kan ta upp till ett dygn innan posterna syns. Vi kontrollerar automatiskt.
            </span>
            <div className="knappar">
              <a className="btn btn-s" href={instruktioner(s)}>
                Skicka instruktioner till er IT
              </a>
              <button
                type="button"
                className="btn btn-s"
                disabled={kontrollera.isPending}
                onClick={() => kontrollera.mutate()}
              >
                {kontrollera.isPending ? 'Kontrollerar …' : 'Kontrollera nu'}
              </button>
              {kanAndra && (
                <button
                  type="button"
                  className="btn btn-g farlig"
                  disabled={taBort.isPending}
                  onClick={() => {
                    if (window.confirm(`Sluta skicka från ${s.domain}?`)) taBort.mutate();
                  }}
                >
                  Sluta använda domänen
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
